import { useCallback, useMemo } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Select,
  Text,
  VStack,
  useDisclosure,
} from '@chakra-ui/react';

import SectionCard from '../../components/SectionCard';
import ValidatedField from '../../components/ValidatedField';
import CurrencyInput from '../../components/CurrencyInput';
import AllPropertyCoveragesModal from '../../components/AllPropertyCoveragesModal';
import HabitationalLiabilityWarningModal from '../../components/HabitationalLiabilityWarningModal';
import HabitationalUnderwritingApprovalModal from '../../components/HabitationalUnderwritingApprovalModal';
import {
  useAnswerCodes,
  useHabitationalLiabilityLimitConfirmation,
  ValidationEntityProvider,
} from '../../hooks';
import type { Location, LocationCoverages } from 'api/quote-marketplace';
import {
  MIDDLE_SECTION_COVERAGES,
  WATER_COVERAGES,
  SEWER_BACKUP_COVERAGE,
  COVERAGE_DEDUCTIBLE_OPTIONS,
  HABITATIONAL_LIABILITY_LIMIT_OPTIONS,
  createEmptyLocationCoverages,
  type HabitationalCoverageDefinition,
} from '../../constants/habitationalCoveragesOptions';

interface ExtendedCoveragesCardProps {
  location: Location;
  locationIndex: number;
  onUpdate: (index: number, field: keyof Location, value: unknown) => void;
  isUpdating?: boolean;
  quoteId: number;
  province: number;
}

const ExtendedCoveragesCard = ({
  location,
  locationIndex,
  onUpdate,
  isUpdating,
  quoteId,
  province,
}: ExtendedCoveragesCardProps) => {
  const {
    getOptions,
    getCodeByLabel,
    isLoading: isLoadingCodes,
  } = useAnswerCodes();

  const {
    isOpen: isModalOpen,
    onOpen: onModalOpen,
    onClose: onModalClose,
  } = useDisclosure();

  const comprehensiveCode = useMemo(
    () => getCodeByLabel('Comprehensive', 'location_form_type'),
    [getCodeByLabel],
  );

  const coverages: LocationCoverages =
    location.coverages || createEmptyLocationCoverages();

  const handleLiabilityLimitApply = useCallback(
    (newValue: number | null) => {
      const updatedCoverages = { ...coverages, liability_limit_cad: newValue };
      onUpdate(locationIndex, 'coverages', updatedCoverages);
    },
    [coverages, locationIndex, onUpdate],
  );

  const {
    handleLiabilityLimitChange,
    warningModalProps,
    underwritingModalProps,
  } = useHabitationalLiabilityLimitConfirmation({
    quoteId,
    province,
    currentLiabilityLimit: coverages.liability_limit_cad,
    onApplyChange: handleLiabilityLimitApply,
  });

  const updateCoverage = useCallback(
    <K extends keyof LocationCoverages>(
      key: K,
      value: LocationCoverages[K],
    ) => {
      const updatedCoverages = { ...coverages, [key]: value };
      onUpdate(locationIndex, 'coverages', updatedCoverages);
    },
    [coverages, locationIndex, onUpdate],
  );

  const handleCheckboxChange = useCallback(
    (def: HabitationalCoverageDefinition, checked: boolean) => {
      if (def.enabledKey) {
        onUpdate(locationIndex, 'coverages', {
          ...coverages,
          [def.enabledKey]: checked,
        });
      }
    },
    [coverages, locationIndex, onUpdate],
  );

  const handleModalSave = useCallback(
    (updatedCoverages: LocationCoverages) => {
      onUpdate(locationIndex, 'coverages', updatedCoverages);
    },
    [locationIndex, onUpdate],
  );

  const isEnabled = (def: HabitationalCoverageDefinition): boolean => {
    if (!def.enabledKey) return true;
    return coverages[def.enabledKey] as boolean;
  };

  const renderCoverageRow = (def: HabitationalCoverageDefinition) => {
    const enabled = isEnabled(def);
    const limitValue = def.limitKey
      ? (coverages[def.limitKey] as number | null)
      : null;

    return (
      <Flex
        key={def.key}
        gap={2}
        align="center"
        wrap="wrap"
        py={1}
        opacity={enabled ? 1 : 0.5}
      >
        <Checkbox
          isChecked={enabled}
          onChange={(e) => handleCheckboxChange(def, e.target.checked)}
          isDisabled={isUpdating}
          size="md"
          colorScheme="blue"
        />
        <Box w="90px">
          <CurrencyInput
            value={limitValue ?? ''}
            onChange={(_, value) => {
              if (def.limitKey) {
                updateCoverage(def.limitKey, value as never);
              }
            }}
            isDisabled={isUpdating || !enabled}
            placeholder={enabled ? 'BASE' : ''}
          />
        </Box>
        <Text fontSize="sm" flex="1">
          {def.name}
        </Text>
      </Flex>
    );
  };

  return (
    <ValidationEntityProvider
      entity={location as unknown as Record<string, unknown>}
    >
      <SectionCard padding={0}>
        <Box
          paddingX={4}
          paddingY={2}
          mb={3}
          bg="gray.200"
          roundedTop="md"
          fontWeight="700"
          color="gray.500"
        >
          Coverages
        </Box>

        <Box paddingX={4} mb={3}>
          <Flex gap={4} wrap="wrap" align="flex-end">
            <ValidatedField
              itemPath="location"
              fieldPath="coverages.coverage_form_type"
              label="Coverage"
              labelSize="xs"
              formControlProps={{ w: '160px' }}
            >
              <Select
                variant="main"
                size="sm"
                height={30}
                value={coverages.coverage_form_type || comprehensiveCode}
                onChange={(e) =>
                  updateCoverage('coverage_form_type', e.target.value)
                }
                isDisabled={isUpdating || isLoadingCodes}
              >
                {getOptions('location_form_type')
                  .filter((opt) => !opt.label.includes('[SK only]'))
                  .map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
              </Select>
            </ValidatedField>

            <ValidatedField
              itemPath="location/coverages"
              fieldPath="coverages.deductible_cad"
              label="Deductible"
              labelSize="xs"
              formControlProps={{ w: '100px' }}
            >
              <Select
                variant="main"
                size="sm"
                height={30}
                value={coverages.deductible_cad ?? ''}
                onChange={(e) =>
                  updateCoverage(
                    'deductible_cad',
                    e.target.value ? Number(e.target.value) : null,
                  )
                }
                isDisabled={isUpdating}
              >
                <option value="">Select...</option>
                {COVERAGE_DEDUCTIBLE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Select>
            </ValidatedField>

            <ValidatedField
              itemPath="liability"
              fieldPath="coverages.liability_limit_cad"
              label="Liability"
              labelSize="xs"
              formControlProps={{ w: '130px' }}
            >
              <Select
                variant="main"
                size="sm"
                height={30}
                value={coverages.liability_limit_cad ?? ''}
                onChange={(e) =>
                  handleLiabilityLimitChange(
                    e.target.value ? Number(e.target.value) : null,
                  )
                }
                isDisabled={isUpdating}
              >
                <option value="">Select...</option>
                {HABITATIONAL_LIABILITY_LIMIT_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Select>
            </ValidatedField>

            <VStack spacing={1} align="flex-start">
              <Checkbox
                isChecked={coverages.grbc_enabled}
                onChange={(e) =>
                  updateCoverage('grbc_enabled', e.target.checked)
                }
                isDisabled={isUpdating}
                size="md"
                colorScheme="blue"
              >
                <Text fontSize="sm">GRBC</Text>
              </Checkbox>
              <Checkbox
                isChecked={coverages.single_limit_enabled}
                onChange={(e) =>
                  updateCoverage('single_limit_enabled', e.target.checked)
                }
                isDisabled={isUpdating}
                size="md"
                colorScheme="blue"
              >
                <Text fontSize="sm">Single Limit</Text>
              </Checkbox>
            </VStack>
          </Flex>
          <Checkbox
            isChecked={coverages.claim_free_protection_endorsement}
            onChange={(e) =>
              updateCoverage(
                'claim_free_protection_endorsement',
                e.target.checked,
              )
            }
            isDisabled={isUpdating}
            size="md"
            colorScheme="blue"
            mt={2}
          >
            <Text fontSize="sm">Claim Free Protection</Text>
          </Checkbox>
        </Box>

        <Divider />

        <VStack align="stretch" spacing={0} paddingX={4} py={2}>
          {MIDDLE_SECTION_COVERAGES.map((def) => renderCoverageRow(def))}
        </VStack>

        <Divider />

        <VStack align="stretch" spacing={0} paddingX={4} py={2}>
          <Box>
            {renderCoverageRow(SEWER_BACKUP_COVERAGE)}
            {coverages.sewer_backup_enabled && (
              <Flex gap={2} ml={6} mt={1} mb={2} wrap="wrap">
                <FormControl w="80px">
                  <FormLabel fontSize="xs" mb={0.5} whiteSpace="nowrap">
                    Deductible
                  </FormLabel>
                  <CurrencyInput
                    value={coverages.sewer_backup_deductible_cad ?? ''}
                    onChange={(_, value) =>
                      updateCoverage('sewer_backup_deductible_cad', value)
                    }
                    isDisabled={isUpdating}
                    placeholder="BASE"
                    height={26}
                  />
                </FormControl>
              </Flex>
            )}
          </Box>
          {WATER_COVERAGES.filter(
            (def) => def.key !== 'sewer_backup_enabled',
          ).map((def) => renderCoverageRow(def))}
        </VStack>

        <Button
          w="100%"
          size="lg"
          bg="blue.700"
          color="white"
          _hover={{ bg: 'blue.800' }}
          roundedTop="0"
          roundedBottom="md"
          onClick={onModalOpen}
          isDisabled={isUpdating}
        >
          All Coverages
        </Button>

        <AllPropertyCoveragesModal
          isOpen={isModalOpen}
          onClose={onModalClose}
          onSave={handleModalSave}
          initialCoverages={coverages}
          location={location}
          quoteId={quoteId}
          province={province}
        />

        <HabitationalLiabilityWarningModal {...warningModalProps} />
        <HabitationalUnderwritingApprovalModal {...underwritingModalProps} />
      </SectionCard>
    </ValidationEntityProvider>
  );
};

export default ExtendedCoveragesCard;
